import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { ArrowBack, ArrowForward, Close, SyncAlt } from "@mui/icons-material";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import {
  applyTandemAssignment,
  candidateLabel,
  dominantOfLink,
  endpointsEqual,
  findCandidate,
  findLink,
  findPartner,
  listTandemCandidates
} from "../../../../Domain/Types/FloorPlan/tandem-link.functions";
import {
  TandemBookingMode,
  TandemCandidate,
  TandemEndpoint,
  TandemObjectKind
} from "../../../../Domain/Types/FloorPlan/TandemLink.type";
import { namePerType } from "../AssignBox/AssignBox";
import { useInventoryManagementActions } from "../../../../Hooks/useInventoryManagement";
import { CONFERENCE_ZONE_TYPE_ID } from "../../../../../../features/Booking-Form/typings/place-and-zone-type-ids.constant";

type Props = {
  self: TandemEndpoint;
  floorPlan: IFloorPayload;
  onClose: () => void;
  inventoryManagement: useInventoryManagementActions;
};

function resolveSelfCandidate(
  floorPlan: IFloorPayload,
  self: TandemEndpoint,
  t: TFunction
): TandemCandidate {
  if (self.kind === TandemObjectKind.PLACE) {
    const place = (floorPlan.places ?? []).find(
      item => (item.inventory?.id ?? item.inventoryId) === self.inventoryId
    );
    const typeName =
      place?.inventory?.placeType?.name ??
      floorPlan.placeTypes?.find(type => type.id === place?.inventory?.placeTypeId)?.name ??
      "workplace";
    return {
      kind: TandemObjectKind.PLACE,
      inventoryId: self.inventoryId,
      name: place?.inventory?.name ?? String(self.inventoryId),
      typeName: namePerType(typeName, t)
    };
  }

  const zone = (floorPlan.zones ?? []).find(
    item => (item.inventory?.id ?? item.inventoryId) === self.inventoryId
  );
  return {
    kind: TandemObjectKind.ZONE,
    inventoryId: self.inventoryId,
    name: zone?.inventory?.name ?? String(self.inventoryId),
    typeName: namePerType(
      zone?.zoneTypeId === CONFERENCE_ZONE_TYPE_ID ||
        zone?.inventory?.zoneTypeId === CONFERENCE_ZONE_TYPE_ID
        ? "conferencezone"
        : "placezone",
      t
    )
  };
}

function initialModeAndGate(
  floorPlan: IFloorPayload,
  self: TandemEndpoint
): { mode: TandemBookingMode; gateIsSelf: boolean } {
  const link = findLink(floorPlan.tandemLinks, self);
  const dominant = link ? dominantOfLink(link) : null;
  if (!dominant) {
    return { mode: "equal", gateIsSelf: true };
  }
  return { mode: "oneWay", gateIsSelf: endpointsEqual(dominant, self) };
}

export function TandemAssignDialogContent({
  self,
  floorPlan,
  onClose,
  inventoryManagement
}: Readonly<Props>) {
  const { t } = useTranslation();
  const candidates = listTandemCandidates(floorPlan, self);
  const currentPartner = findPartner(floorPlan.tandemLinks, self);
  const initial = initialModeAndGate(floorPlan, self);
  const [selected, setSelected] = useState<TandemCandidate | null>(
    findCandidate(candidates, currentPartner)
  );
  const [mode, setMode] = useState<TandemBookingMode>(initial.mode);
  const [gateIsSelf, setGateIsSelf] = useState(initial.gateIsSelf);
  const [pendingPartner, setPendingPartner] = useState<TandemCandidate | null | undefined>(
    undefined
  );

  const labeledCandidates = candidates.map(candidate => ({
    ...candidate,
    typeName: namePerType(candidate.typeName, t)
  }));

  const selfCandidate = useMemo(
    () => resolveSelfCandidate(floorPlan, self, t),
    [floorPlan, self, t]
  );

  const selectedValue = selected
    ? labeledCandidates.find(
        option => option.kind === selected.kind && option.inventoryId === selected.inventoryId
      ) ?? null
    : null;

  const resolveDominant = (partner: TandemCandidate | null): TandemEndpoint | null => {
    if (!partner || mode === "equal") {
      return null;
    }
    return gateIsSelf ? self : { kind: partner.kind, inventoryId: partner.inventoryId };
  };

  const applyAndClose = (partner: TandemCandidate | null) => {
    inventoryManagement.changeTandemPartner(
      self,
      partner ? { kind: partner.kind, inventoryId: partner.inventoryId } : null,
      resolveDominant(partner)
    );
    onClose();
  };

  const handleSave = () => {
    const partnerEndpoint = selected
      ? { kind: selected.kind, inventoryId: selected.inventoryId }
      : null;
    const { steals } = applyTandemAssignment(floorPlan.tandemLinks, self, partnerEndpoint, {
      dominant: resolveDominant(selected)
    });
    const replacingExisting = steals.length > 0 && partnerEndpoint !== null;
    if (replacingExisting) {
      setPendingPartner(selected);
      return;
    }
    applyAndClose(selected);
  };

  const descriptionKey = mode === "equal" ? "_tandemAssign_descEqual" : "_tandemAssign_descOneWay";

  const gateLabel = gateIsSelf ? selfCandidate.name : selectedValue?.name ?? "";
  const dependentLabel = gateIsSelf ? selectedValue?.name ?? "" : selfCandidate.name;

  return (
    <Card sx={{ p: 2, maxHeight: "80vh", minWidth: 400, width: "30vw", maxWidth: 560 }}>
      <Stack direction={"column"} gap={2}>
        <Box>
          <Typography align={"center"} lineHeight={2.5}>
            {t("Tandem")}
            <IconButton
              sx={{ float: "right" }}
              data-testid="tandem-assign-close"
              onClick={onClose}
              size="large"
            >
              <Close />
            </IconButton>
          </Typography>
          <Typography
            align={"left"}
            color="text.secondary"
            sx={{ fontSize: 13, px: 0.5, lineHeight: 1.4 }}
          >
            {t("_tandemAssign_helper")}
          </Typography>
        </Box>

        <Autocomplete
          multiple
          data-testid="tandem-assign-autocomplete"
          options={labeledCandidates}
          value={selectedValue ? [selectedValue] : []}
          onChange={(_event, next) => {
            setSelected(next.at(-1) ?? null);
          }}
          getOptionLabel={option => candidateLabel(option)}
          isOptionEqualToValue={(option, value) =>
            option.kind === value.kind && option.inventoryId === value.inventoryId
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  size="small"
                  label={candidateLabel(option)}
                  data-testid="tandem-assign-chip"
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              placeholder={selectedValue ? undefined : t("_tandemAssign_autocompleteLabel")}
            />
          )}
        />

        {selectedValue && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap={1}
              data-testid="tandem-mode-switch-row"
            >
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: mode === "equal" ? 600 : 400,
                  color: mode === "equal" ? "text.primary" : "text.secondary"
                }}
              >
                {t("_tandemAssign_modeEqual")}
              </Typography>
              <Switch
                size="small"
                checked={mode === "oneWay"}
                onChange={(_event, checked) => setMode(checked ? "oneWay" : "equal")}
                inputProps={{ "aria-label": t("_tandemAssign_modeOneWay") }}
                data-testid="tandem-mode-switch"
              />
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: mode === "oneWay" ? 600 : 400,
                  color: mode === "oneWay" ? "text.primary" : "text.secondary"
                }}
              >
                {t("_tandemAssign_modeOneWay")}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap={1.5}
              data-testid="tandem-wire-preview"
              sx={{ px: 0.5, py: 0.5 }}
            >
              <Chip
                label={selfCandidate.name}
                color={mode === "oneWay" && gateIsSelf ? "primary" : "default"}
                onClick={mode === "oneWay" ? () => setGateIsSelf(true) : undefined}
                data-testid="tandem-wire-self"
                sx={{ maxWidth: "38%", height: 36, fontSize: 14 }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  flexShrink: 0,
                  minWidth: 40
                }}
                data-testid="tandem-wire-connector"
              >
                {mode === "equal" && <SyncAlt sx={{ fontSize: 36 }} />}
                {mode === "oneWay" && gateIsSelf && <ArrowForward sx={{ fontSize: 36 }} />}
                {mode === "oneWay" && !gateIsSelf && <ArrowBack sx={{ fontSize: 36 }} />}
              </Box>
              <Chip
                label={selectedValue.name}
                color={mode === "oneWay" && !gateIsSelf ? "primary" : "default"}
                onClick={mode === "oneWay" ? () => setGateIsSelf(false) : undefined}
                data-testid="tandem-wire-partner"
                sx={{ maxWidth: "38%", height: 36, fontSize: 14 }}
              />
            </Stack>

            <Box
              data-testid="tandem-mode-description"
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
                border: theme => `1px solid ${theme.palette.divider}`
              }}
            >
              <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                {mode === "equal"
                  ? t(descriptionKey, {
                      a: selfCandidate.name,
                      b: selectedValue.name
                    })
                  : t(descriptionKey, {
                      gate: gateLabel,
                      dependent: dependentLabel
                    })}
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button data-testid="tandem-assign-save-btn" onClick={handleSave} sx={{ width: "100%" }}>
            {t("Save")}
          </Button>
        </Box>
      </Stack>

      <Dialog
        open={pendingPartner !== undefined}
        onClose={() => setPendingPartner(undefined)}
        data-testid="tandem-steal-dialog"
      >
        <DialogTitle>{t("_tandemAssign_replaceTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("_tandemAssign_replaceBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-testid="tandem-steal-cancel" onClick={() => setPendingPartner(undefined)}>
            {t("Cancel")}
          </Button>
          <Button
            data-testid="tandem-steal-confirm"
            onClick={() => {
              applyAndClose(pendingPartner ?? null);
              setPendingPartner(undefined);
            }}
          >
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
